// Cross-tool "needs attention" signals for the sidebar counts, the top
// bar's health line and the Dashboard list. Each source is only fetched
// when the signed-in user has that tool's permission, and a source that
// fails to load is simply left out: this is a convenience summary, the
// tools themselves remain the source of truth.
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { authenticatedFetch } from './AuthComponent';

const REFRESH_MS = 2 * 60 * 1000;
const CRON_LOOKBACK_HOURS = 24;

const AlertsContext = createContext({ alerts: null, refresh: () => {} });
export const useAlerts = () => useContext(AlertsContext);

async function loadOffboarding() {
  const [attnResp, delResp] = await Promise.all([
    authenticatedFetch('/api/offboarding/full-run/history?needs_attention=true'),
    authenticatedFetch('/api/offboarding/deletion/candidates'),
  ]);
  const attention = attnResp.ok ? ((await attnResp.json()).runs || []) : null;
  const deletion = delResp.ok ? ((await delResp.json()).rows || []).filter((r) => !r.deleted_on) : null;
  return { attention, deletion };
}

// A job is "failing" when its most recent run in the window failed — an
// earlier failure that a later run recovered from isn't worth a red dot.
// Plain fetch, not authenticatedFetch: cron-monitor is a separate service,
// and a 401 from it must not log the user out of the whole hub.
async function loadCron() {
  const token = localStorage.getItem('access_token');
  const start = new Date(Date.now() - CRON_LOOKBACK_HOURS * 3600 * 1000).toISOString().slice(0, 19);
  const end = new Date(Date.now() + 60 * 1000).toISOString().slice(0, 19);
  const resp = await fetch(`/api/cron/runs?start_date=${start}&end_date=${end}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!resp.ok) return null;
  const runs = (await resp.json()).runs || [];
  const latest = {};
  for (const run of runs) {
    const prev = latest[run.job_name];
    if (!prev || String(run.started_at) > String(prev.started_at)) latest[run.job_name] = run;
  }
  return Object.values(latest).filter((r) => r.status === 'failed');
}

// Products waiting on open orders before a location can be unstocked,
// and products where a step failed.
async function loadTeamaticalPrep() {
  const resp = await authenticatedFetch('/api/teamatical-prep/history');
  if (!resp.ok) return null;
  const runs = (await resp.json()).runs || [];
  return runs.filter((r) => r.status === 'pending_cleanup' || r.status === 'failed');
}

// Products the latest audit says need attention (drift, duplicate IDs).
async function loadTeamaticalAudit() {
  const resp = await authenticatedFetch('/api/teamatical-prep/audit');
  if (!resp.ok) return null;
  const audit = (await resp.json()).audit;
  return audit ? (audit.rows || []).filter((r) => r.group === 'attention') : [];
}

// BOPIS automatic open/close runs that are retrying or gave up.
async function loadBopisAlerts(api) {
  const resp = await authenticatedFetch(`${api}/scheduler`);
  if (!resp.ok) return null;
  return (await resp.json()).alerts || [];
}

async function loadPurchaseOrderSession() {
  const resp = await authenticatedFetch('/api/purchase-orders/status');
  if (resp.status === 403) return null;
  if (!resp.ok) return { ok: false };
  const data = await resp.json();
  return { ok: data.status === 'ok', age: data.config_age || '' };
}

// Orders the B2B review flagged that nobody has looked at yet.
async function loadB2BReview() {
  const resp = await authenticatedFetch('/api/b2b-order-review/status');
  if (!resp.ok) return null;
  return (await resp.json()).stats.need_review;
}

const settle = (p) => p.then((v) => v, () => null);

export function AlertsProvider({ permissions, children }) {
  const [alerts, setAlerts] = useState(null);
  const has = useCallback((id) => (permissions || []).includes(id), [permissions]);

  const refresh = useCallback(async () => {
    const [offboarding, cronFailing, poSession, teamatical, teamaticalAudit, gothamBopis, spiritBopis, b2bReview] = await Promise.all([
      has('offboarding') ? settle(loadOffboarding()) : null,
      has('cron-monitor') ? settle(loadCron()) : null,
      has('purchase-orders') ? settle(loadPurchaseOrderSession()) : null,
      has('teamatical-prep') ? settle(loadTeamaticalPrep()) : null,
      has('teamatical-prep') ? settle(loadTeamaticalAudit()) : null,
      has('gotham-bopis') ? settle(loadBopisAlerts('/api/gotham-bopis')) : null,
      has('wa-spirit-bopis') ? settle(loadBopisAlerts('/api/spirit-bopis')) : null,
      has('b2b-order-review') ? settle(loadB2BReview()) : null,
    ]);
    setAlerts({ offboarding, cronFailing, poSession, teamatical, teamaticalAudit, gothamBopis, spiritBopis, b2bReview });
  }, [has]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh]);

  return <AlertsContext.Provider value={{ alerts, refresh }}>{children}</AlertsContext.Provider>;
}

// Per-tool nav counts: { [toolId]: { count, tone: 'crit' | 'warn', label } }.
export function navBadges(alerts) {
  const out = {};
  if (!alerts) return out;
  const ob = alerts.offboarding;
  const obCount = (ob?.attention?.length || 0);
  if (obCount) out.offboarding = { count: obCount, tone: 'warn', label: `${obCount} need${obCount === 1 ? 's' : ''} attention` };
  if (alerts.cronFailing?.length) {
    const n = alerts.cronFailing.length;
    out['cron-monitor'] = { count: n, tone: 'crit', label: `${n} job${n === 1 ? '' : 's'} failing` };
  }
  const tpFailed = (alerts.teamatical || []).filter((r) => r.status === 'failed').length;
  const tpAudit = alerts.teamaticalAudit?.length || 0;
  const tpWaiting = (alerts.teamatical || []).length - tpFailed;
  if (tpFailed || tpAudit || tpWaiting) {
    // Waiting on open orders clears itself nightly, so it only shows when nothing else does.
    const n = tpFailed + tpAudit || tpWaiting;
    out['teamatical-prep'] = {
      count: n, tone: tpFailed ? 'crit' : 'warn',
      label: [tpFailed && `${tpFailed} failed`, tpAudit && `${tpAudit} audit finding${tpAudit === 1 ? '' : 's'}`].filter(Boolean).join(' · ')
        || `${tpWaiting} waiting on open orders`,
    };
  }
  [['gotham-bopis', alerts.gothamBopis], ['wa-spirit-bopis', alerts.spiritBopis]].forEach(([id, list]) => {
    if (!list?.length) return;
    const failed = list.filter((a) => a.status === 'failed').length;
    out[id] = {
      count: list.length, tone: failed ? 'crit' : 'warn',
      label: failed ? `Automatic run failed${failed > 1 ? ` (${failed})` : ''}` : 'Automatic run retrying',
    };
  });
  if (alerts.b2bReview) {
    out['b2b-order-review'] = { count: alerts.b2bReview, tone: 'warn', label: `${alerts.b2bReview} order${alerts.b2bReview === 1 ? '' : 's'} flagged` };
  }
  if (alerts.poSession && !alerts.poSession.ok) {
    out['purchase-orders'] = { count: '!', tone: 'warn', label: 'Shopify session expired' };
  }
  return out;
}
